import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["jsonViewerModal", "jsonEditorContainer", "modalVinNumber"];
  static values = { rawResponses: Object };

  connect() {
    this.vinStates = {};
    this.jsonEditor = null;
    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundResize = this.handleResize.bind(this);
    document.addEventListener("keydown", this.boundKeydown);
    window.addEventListener("resize", this.boundResize);
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown);
    window.removeEventListener("resize", this.boundResize);
    if (this.jsonEditor) {
      this.jsonEditor.dispose();
      this.jsonEditor = null;
    }
  }

  toggleDetails(event) {
    const vinInfoId = event.currentTarget.dataset.vinInfoId;
    const currentState = this.vinStates[vinInfoId] || "collapsed";

    if (currentState === "editing") return;

    if (currentState === "collapsed") {
      this.showDetailsView(vinInfoId);
    } else if (currentState === "viewing") {
      this.collapseVin(vinInfoId);
    }
  }

  handleEdit(event) {
    const vinInfoId = event.currentTarget.dataset.vinInfoId;
    const currentState = this.vinStates[vinInfoId] || "collapsed";

    if (currentState === "editing") {
      this.collapseVin(vinInfoId);
    } else {
      this.showEditMode(vinInfoId);
    }
  }

  cancelEdit(event) {
    const vinInfoId = event.currentTarget.dataset.vinInfoId;
    this.showDetailsView(vinInfoId);
  }

  showDetailsView(vinInfoId) {
    const detailsDiv = document.getElementById(`vin-details-${vinInfoId}`);
    const readonlyDiv = document.getElementById(`readonly-${vinInfoId}`);
    const editForm = document.getElementById(`edit-form-${vinInfoId}`);
    const expandText = document.getElementById(`expand-text-${vinInfoId}`);
    const editText = document.getElementById(`edit-text-${vinInfoId}`);

    editForm.classList.add("hidden");
    setTimeout(() => {
      detailsDiv.classList.remove("hidden");
      readonlyDiv.classList.remove("hidden");
      expandText.textContent = "Hide";
      editText.textContent = "Edit";
      this.vinStates[vinInfoId] = "viewing";
    }, 50);
  }

  showEditMode(vinInfoId) {
    const detailsDiv = document.getElementById(`vin-details-${vinInfoId}`);
    const readonlyDiv = document.getElementById(`readonly-${vinInfoId}`);
    const editForm = document.getElementById(`edit-form-${vinInfoId}`);
    const expandText = document.getElementById(`expand-text-${vinInfoId}`);
    const editText = document.getElementById(`edit-text-${vinInfoId}`);

    readonlyDiv.classList.add("hidden");
    setTimeout(() => {
      detailsDiv.classList.remove("hidden");
      editForm.classList.remove("hidden");
      expandText.textContent = "Hide";
      editText.textContent = "Cancel";
      this.vinStates[vinInfoId] = "editing";
    }, 50);
  }

  collapseVin(vinInfoId) {
    const detailsDiv = document.getElementById(`vin-details-${vinInfoId}`);
    const expandText = document.getElementById(`expand-text-${vinInfoId}`);
    const editText = document.getElementById(`edit-text-${vinInfoId}`);

    detailsDiv.classList.add("hidden");
    expandText.textContent = "Details";
    editText.textContent = "Edit";
    this.vinStates[vinInfoId] = "collapsed";
  }

  openJsonViewer(event) {
    const vinInfoId = event.currentTarget.dataset.vinInfoId;
    const vinRow = document.querySelector(
      `tr[data-vin-info-id="${vinInfoId}"]`
    );
    const vinNumber = vinRow.querySelector("code").textContent.trim();
    const rawResponse = this.rawResponsesValue[vinInfoId];

    if (!rawResponse) {
      this.showNotification("No raw response data available", "error");
      return;
    }

    this.modalVinNumberTarget.textContent = vinNumber;
    this.jsonViewerModalTarget.classList.remove("hidden");

    window.require(["vs/editor/editor.main"], () => {
      const container = this.jsonEditorContainerTarget;

      if (this.jsonEditor) {
        this.jsonEditor.dispose();
      }

      container.innerHTML = "";

      this.jsonEditor = monaco.editor.create(container, {
        value: JSON.stringify(rawResponse, null, 2),
        language: "json",
        theme: "vs-dark",
        readOnly: true,
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        wordWrap: "on",
        fontSize: 12,
        lineNumbers: "on",
        folding: true,
        contextmenu: false,
      });

      setTimeout(() => this.jsonEditor?.layout(), 100);
    });
  }

  closeJsonViewer() {
    this.jsonViewerModalTarget.classList.add("hidden");
    if (this.jsonEditor) {
      this.jsonEditor.dispose();
      this.jsonEditor = null;
    }
  }

  copyJson() {
    if (this.jsonEditor) {
      navigator.clipboard.writeText(this.jsonEditor.getValue()).then(() => {
        this.showNotification("JSON copied to clipboard!", "success");
      });
    }
  }

  closeJsonOnBackdrop(event) {
    if (event.target === this.jsonViewerModalTarget) {
      this.closeJsonViewer();
    }
  }

  submitVinForm(event) {
    event.preventDefault();

    const form = event.target;
    const formData = new FormData(form);

    const submitButton = form.querySelector('button[type="submit"]');
    const originalText = submitButton.innerHTML;
    submitButton.innerHTML =
      '<svg class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>Saving...';
    submitButton.disabled = true;

    fetch(form.action, {
      method: "PATCH",
      body: formData,
      headers: {
        "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.showNotification(
            "VIN information updated successfully!",
            "success"
          );
          submitButton.innerHTML = originalText;
          submitButton.disabled = false;

          const vinInfoId = form
            .closest("tr[data-vin-info-id]")
            .dataset.vinInfoId;
          setTimeout(() => {
            this.showDetailsView(vinInfoId);
          }, 500);
        } else {
          this.showNotification(data.error || "An error occurred", "error");
          submitButton.innerHTML = originalText;
          submitButton.disabled = false;
        }
      })
      .catch(() => {
        this.showNotification("An error occurred while saving", "error");
        submitButton.innerHTML = originalText;
        submitButton.disabled = false;
      });
  }

  dismissNotification(event) {
    event.currentTarget.closest("[data-notification]").remove();
  }

  showNotification(message, type = "info") {
    const notification = document.createElement("div");
    notification.dataset.notification = true;
    notification.className = `fixed top-4 right-4 z-50 p-4 rounded-lg shadow-xl max-w-sm transition-all duration-300 ${
      type === "success"
        ? "bg-green-100 border border-green-200 text-green-800"
        : type === "error"
          ? "bg-red-100 border border-red-200 text-red-800"
          : "bg-blue-100 border border-blue-200 text-blue-800"
    }`;

    notification.innerHTML = `
      <div class="flex items-center">
        <div class="flex-1">
          <p class="text-sm font-medium">${message}</p>
        </div>
        <button type="button" data-action="admin-search-show#dismissNotification" class="ml-3 text-gray-400 hover:text-gray-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;

    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 5000);
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.hasJsonViewerModalTarget) {
      if (!this.jsonViewerModalTarget.classList.contains("hidden")) {
        this.closeJsonViewer();
      }
    }
  }

  handleResize() {
    if (this.jsonEditor) {
      this.jsonEditor.layout();
    }
  }
}
